// ============================================
// Quality Gate: Document Audit & Auto-Fix
//
// v5 — UC-7: Semantic completeness scoring
//
// Two-layer validation:
//   Option A — AI self-check appended to prompt
//   Option B — Programmatic keyword scorer
//
// v5 additions:
//   - Content depth scoring (not just keyword presence)
//   - Inherited provision coverage verification
//   - Entity reference validation
//   - Pre-sanitize scoring
//   - Progressive fill staging
//   - Plain-English audit summary
//
// Flow: generate → score → if <threshold,
//       regenerate with gaps → max N attempts
// ============================================

import type {
  BlueprintSection,
  QualityGate,
  AuditReport,
  AuditSectionResult,
  FillStage,
  ProvisionCoverageResult,
} from '../../types/docops';
import { INHERITED_PROVISIONS } from '../../data/docops/provisionAlignmentMap';
import { ENTITY_REGISTRY } from '../../data/docops/entityRegistry';

// ── CONTENT DEPTH SCORING (v5) ──

/**
 * Check that a keyword has substantive content around it,
 * not just the keyword sitting alone in a heading.
 * Returns true if there are >= minWords words within a window
 * around the keyword location.
 */
function hasContentDepth(content: string, keyword: string, minWords: number = 30): boolean {
  const lower = content.toLowerCase();
  const kwLower = keyword.toLowerCase();
  const idx = lower.indexOf(kwLower);
  if (idx === -1) return false;

  // Extract a window of 800 chars around the keyword
  const windowStart = Math.max(0, idx - 400);
  const windowEnd = Math.min(content.length, idx + kwLower.length + 400);
  const window = content.slice(windowStart, windowEnd);

  // Count words (excluding markdown formatting)
  const cleaned = window
    .replace(/#{1,6}\s/g, '')
    .replace(/[*_`~\[\]()]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  const wordCount = cleaned.split(' ').filter(w => w.length > 1).length;

  return wordCount >= minWords;
}

// ── PROGRAMMATIC SCORER (Option B) ──

/**
 * Score generated content against blueprint provisions.
 * v5: Enhanced with content depth validation — keyword presence alone
 * is no longer sufficient. Each provision must have substantive content.
 */
export function scoreDocument(
  content: string,
  provisions: BlueprintSection[],
): AuditReport {
  const lower = content.toLowerCase();

  const sections: AuditSectionResult[] = provisions.map((p) => {
    const matched: string[] = [];
    const missing: string[] = [];

    for (const kw of p.keywords) {
      if (lower.includes(kw.toLowerCase())) {
        matched.push(kw);
      } else {
        missing.push(kw);
      }
    }

    // v5: A section passes only if keyword is found AND has substantive content around it
    let found = matched.length > 0;
    if (found && p.weight === 'critical') {
      // For critical provisions, verify content depth around at least one matched keyword
      const hasDepth = matched.some(kw => hasContentDepth(content, kw, 20));
      if (!hasDepth) {
        found = false;
        missing.push('(keyword present but no substantive content around it)');
      }
    }

    return {
      id: p.id,
      title: p.title,
      found,
      weight: p.weight,
      matchedKeywords: matched,
      missingKeywords: missing,
    };
  });

  // Scoring: critical provisions worth 70% of score, high worth 30%
  let criticalHits = 0, criticalTotal = 0;
  let highHits = 0, highTotal = 0;

  for (const s of sections) {
    if (s.weight === 'critical') {
      criticalTotal++;
      if (s.found) criticalHits++;
    } else {
      highTotal++;
      if (s.found) highHits++;
    }
  }

  const criticalScore = criticalTotal > 0 ? (criticalHits / criticalTotal) * 70 : 70;
  const highScore = highTotal > 0 ? (highHits / highTotal) * 30 : 30;
  const score = Math.round(criticalScore + highScore);

  const missingCritical = sections
    .filter((s) => !s.found && s.weight === 'critical')
    .map((s) => s.title);

  const missingHigh = sections
    .filter((s) => !s.found && s.weight === 'high')
    .map((s) => s.title);

  return {
    score,
    passed: false, // caller sets based on gate threshold
    attempt: 0,
    maxAttempts: 0,
    sections,
    missingCritical,
    missingHigh,
    timestamp: new Date().toISOString(),
  };
}

// ── INHERITED PROVISION COVERAGE (v5) ──

/**
 * Check inherited provision coverage for a specific template.
 * Unlike keyword-only scoring, this verifies:
 * 1. ALL critical keywords present (not just one)
 * 2. Substantive content exists around found keywords
 */
export function scoreProvisionCoverage(
  content: string,
  templateId: string,
): ProvisionCoverageResult[] {
  const applicable = INHERITED_PROVISIONS.filter(p =>
    p.appliesTo.includes(templateId),
  );

  if (applicable.length === 0) return [];

  const lower = content.toLowerCase();

  return applicable.map(provision => {
    const found: string[] = [];
    const missing: string[] = [];

    for (const kw of provision.keywords) {
      if (lower.includes(kw.toLowerCase())) {
        found.push(kw);
      } else {
        missing.push(kw);
      }
    }

    // Content depth: at least one keyword must have substantive surrounding content
    const contentDepthPassed = found.length > 0 &&
      found.some(kw => hasContentDepth(content, kw, 25));

    // Score: percentage of keywords found, with depth penalty
    const keywordPct = provision.keywords.length > 0
      ? found.length / provision.keywords.length
      : 0;
    const depthMultiplier = contentDepthPassed ? 1.0 : 0.5;
    const score = Math.round(keywordPct * depthMultiplier * 100);

    return {
      provisionId: provision.id,
      provisionName: provision.name,
      keywordsRequired: provision.keywords,
      keywordsFound: found,
      keywordsMissing: missing,
      contentDepthPassed,
      score,
    };
  });
}

// ── ENTITY REFERENCE VALIDATION (v5) ──

/**
 * Validate entity references in the document against the entity registry.
 * Checks for:
 * - Entities that should appear in this document but don't
 * - "supervises" used with Medical Director (should be "oversight")
 */
export function validateEntityReferences(
  content: string,
  templateId: string,
): string[] {
  const issues: string[] = [];
  const lower = content.toLowerCase();

  for (const entity of ENTITY_REGISTRY.entities) {
    if (!entity.appearsIn.includes(templateId)) continue;

    // Check if at least one valid reference appears
    const hasReference = entity.validReferences.some(ref => {
      // Skip {{variable}} references — they may not be resolved yet
      if (ref.startsWith('{{')) return true;
      return lower.includes(ref.toLowerCase());
    });

    if (!hasReference && entity.legalName) {
      issues.push(
        `Entity "${entity.role}" (${entity.legalName}) should be referenced but was not found`,
      );
    }
  }

  // Check Medical Director language — "oversight" not "supervises"
  if (lower.includes('medical director') && /\bsupervis(?:es|ion|ing|ed)\b/i.test(content)) {
    issues.push(
      'Medical Director described as "supervises" — must use "provides clinical oversight" per CPOM firewall',
    );
  }

  return issues;
}

// ── PRE-SANITIZE SCORING (v5) ──

/**
 * Score content BEFORE the sanitizer runs.
 * Measures true AI output quality by counting issues
 * the sanitizer would mask.
 */
export function scorePreSanitize(rawContent: string): number {
  let deductions = 0;

  // Count unresolved {{variables}} — each costs 2 points (max 20)
  const mustacheCount = (rawContent.match(/\{\{[a-zA-Z]+\}\}/g) || []).length;
  deductions += Math.min(20, mustacheCount * 2);

  // Count [bracket] placeholders — each costs 3 points (max 15)
  const bracketCount = (rawContent.match(/\[(?:INSERT|TBD|PLACEHOLDER|Address|Date|City|State|Name|Company|Entity)\b/gi) || []).length;
  deductions += Math.min(15, bracketCount * 3);

  // Check for AI meta-commentary — 5 points
  if (/(here is the|here's the|i've (created|drafted|generated)|as requested)/i.test(rawContent)) {
    deductions += 5;
  }

  // Check for TODO/FIXME — 5 points each (max 10)
  const todoCount = (rawContent.match(/\bTODO\b|\bFIXME\b/gi) || []).length;
  deductions += Math.min(10, todoCount * 5);

  // Check for duplicate H1 headings — 5 points
  const h1Count = (rawContent.match(/^#\s+[^\n]+$/gm) || []).length;
  if (h1Count > 1) deductions += 5;

  return Math.max(0, 100 - deductions);
}

// ── PROGRESSIVE FILL STAGING (v5) ──

/**
 * Assess the progressive fill stage of a document.
 * Per the vision doc:
 *   Skeleton (10%) → Domain context (40%) → Variables resolved (70%)
 *   → Cross-doc verified (85%) → Gate passed (92%)
 *   → Attorney reviewed (95%) → Signed (100%)
 */
export function assessFillStage(
  content: string,
  auditReport: AuditReport,
  docStatus?: string,
): { stage: FillStage; score: number } {
  // Signed documents
  if (docStatus === 'signed') {
    return { stage: 'signed', score: 100 };
  }

  // Attorney reviewed
  if (docStatus === 'attorney_approved' || docStatus === 'final') {
    return { stage: 'attorney_reviewed', score: 95 };
  }

  // Gate passed
  if (auditReport.passed && auditReport.score >= 92) {
    return { stage: 'gate_passed', score: 92 };
  }

  // Cross-doc verified: provisions present
  const provCoverage = auditReport.provisionCoverage || [];
  const allProvisionsPresent = provCoverage.length === 0 ||
    provCoverage.every(p => p.score >= 70);
  const noVariables = (content.match(/\{\{[a-zA-Z]+\}\}/g) || []).length === 0;

  if (noVariables && allProvisionsPresent && auditReport.score >= 70) {
    return { stage: 'cross_doc_verified', score: 85 };
  }

  // Variables resolved: no {{variables}} remaining
  if (noVariables && auditReport.score >= 50) {
    return { stage: 'variables_resolved', score: 70 };
  }

  // Domain context: has substantive content
  const wordCount = content.replace(/\s+/g, ' ').trim().split(' ').length;
  const headingCount = (content.match(/^#{1,3}\s+/gm) || []).length;
  if (wordCount >= 200 && headingCount >= 3) {
    return { stage: 'domain_context', score: 40 };
  }

  // Skeleton: has headings but minimal content
  return { stage: 'skeleton', score: 10 };
}

// ── PLAIN-ENGLISH SUMMARY (v5) ──

/**
 * Build a plain-English summary of the audit report.
 * For Kevin/Collin — no legal knowledge required to understand.
 */
export function buildGateSummary(report: AuditReport): string {
  const parts: string[] = [];

  if (report.passed) {
    parts.push(`Document passed quality gate (${report.score}%).`);
  } else {
    parts.push(`Document did not pass quality gate (${report.score}%).`);
  }

  if (report.missingCritical.length > 0) {
    parts.push(
      `Missing critical provisions: ${report.missingCritical.join(', ')}.`,
    );
  }

  if (report.missingHigh.length > 0) {
    parts.push(
      `Missing recommended provisions: ${report.missingHigh.join(', ')}.`,
    );
  }

  // Provision coverage
  const provCoverage = report.provisionCoverage || [];
  const failedProvisions = provCoverage.filter(p => p.score < 70);
  if (failedProvisions.length > 0) {
    const names = failedProvisions.map(p => p.provisionName);
    parts.push(
      `Inherited provisions with weak coverage: ${names.join(', ')}. These are compliance requirements that must appear substantively in the document.`,
    );
  }

  // Entity issues
  if (report.entityIssues && report.entityIssues.length > 0) {
    parts.push(
      `Entity reference issues: ${report.entityIssues.join('; ')}.`,
    );
  }

  // Pre-sanitize score
  if (report.preSanitizeScore !== undefined && report.preSanitizeScore < 80) {
    parts.push(
      `Raw AI output quality was ${report.preSanitizeScore}% (before cleanup). The sanitizer fixed formatting issues, but the AI should produce cleaner output.`,
    );
  }

  // Fill stage
  if (report.fillStage) {
    const stageDescriptions: Record<string, string> = {
      skeleton: 'Document is a skeleton — headings only, no substantive content.',
      domain_context: 'Document has content but unresolved template variables remain.',
      variables_resolved: 'Variables are filled but compliance provisions may be missing.',
      cross_doc_verified: 'Provisions present and cross-document consistency verified.',
      gate_passed: 'Document passed all automated quality checks.',
      attorney_reviewed: 'Document has been reviewed by an attorney.',
      signed: 'Document is fully executed.',
    };
    parts.push(stageDescriptions[report.fillStage] || '');
  }

  return parts.filter(Boolean).join(' ');
}

// ── AI SELF-CHECK PROMPT (Option A) ──

/**
 * Build a self-check instruction block appended to every
 * generation prompt. Tells Claude to verify mandatory
 * provisions before finishing.
 */
export function buildSelfCheckPrompt(provisions: BlueprintSection[]): string {
  const critical = provisions.filter((p) => p.weight === 'critical');
  const high = provisions.filter((p) => p.weight === 'high');

  const lines = [
    '',
    '--- QUALITY GATE: SELF-CHECK ---',
    'Before finalizing your output, verify ALL of the following:',
    '',
    'A) KEYWORD PROVISIONS — each must appear in the document:',
    '',
    'CRITICAL (must appear — document fails without these):',
    ...critical.map((p) => `  - ${p.title}: include at least one of [${p.keywords.join(', ')}]`),
    '',
    'HIGH PRIORITY (should appear):',
    ...high.map((p) => `  - ${p.title}: include at least one of [${p.keywords.join(', ')}]`),
    '',
    'B) STRUCTURAL CHECKS — verify these format rules:',
    '',
    '  1. SECTION COMPLETENESS: Every section in the blueprint appears with full content.',
    '     No section is truncated mid-sentence or omitted. If approaching limits,',
    '     compress earlier sections rather than dropping later ones.',
    '',
    '  2. SEQUENTIAL NUMBERING: Sections are numbered 1, 2, 3... with no gaps,',
    '     no duplicates, and no outline stubs or topic headers between sections.',
    '',
    '  3. VARIABLE FORMAT: ALL placeholder values use {{mustache}} format.',
    '     NEVER use [INSERT], [TBD], [PLACEHOLDER], [Address], [Date], [City, State ZIP],',
    '     or ANY bracket notation for missing values. Use {{camelCaseVar}} instead.',
    '     Example: {{businessAddress}} not [Address], {{effectiveDate}} not [Date].',
    '',
    '  4. ENTITY ALIGNMENT: Use ONLY entity names from the brand config.',
    '     Do not reference dormant or backup entities.',
    '     Use "provides clinical oversight" — never "supervises."',
    '',
    '  5. NO META-COMMENTARY: The output must end with the final section of the document',
    '     (e.g., "Contact Us" or "General Provisions") and NOTHING ELSE.',
    '     Do NOT append any postscript, footnote, variable listing, "NEW VARIABLES NEEDED",',
    '     checklists, summaries, notes, or self-referential text after the document.',
    '     The output must be a clean, attorney-ready legal document with no trailing commentary.',
    '',
    'C) CONTENT DEPTH: Each provision must have substantive content — not just a heading or keyword.',
    '   Every section should contain at least 2-3 sentences of legally meaningful content.',
    '',
    'If any CRITICAL provision is missing, add it before finishing.',
    'Do NOT include this checklist in the output — it is for your internal verification only.',
    '--- END QUALITY GATE ---',
  ];

  return lines.join('\n');
}

// ── PROVISION REMEDIATION MAP ──
// Maps provision IDs to specific actionable fix instructions.
// Generic "missing keywords" doesn't help for structural issues —
// Claude needs to know WHAT TO DO DIFFERENTLY, not just what's missing.

const PROVISION_REMEDIATION: Record<string, string> = {
  // Fallback gate structural fixes
  'fb-no-brackets':
    'CRITICAL FIX — BRACKET PLACEHOLDERS: Your draft contains [bracket] placeholders like [Company Name], [Address], [Date], [State], etc. ' +
    'REMOVE EVERY bracket placeholder. For values you know from the brand config, substitute the actual value. ' +
    'For values you do not know, use {{camelCaseVariable}} mustache format (e.g., {{businessAddress}}, {{effectiveDate}}). ' +
    'NEVER use [ANY BRACKET NOTATION] for any reason.',

  'fb-no-mustache':
    'CRITICAL FIX — UNREPLACED VARIABLES: Your draft contains {{variable}} placeholders that should have been filled in. ' +
    'Use the actual values from the brand config provided in the system prompt. ' +
    'Only use {{variable}} format for values genuinely not available in the config.',

  'fb-brand-name':
    'CRITICAL FIX — MISSING BRAND NAME: Your draft does not reference the brand by name. ' +
    'The brand name MUST appear in the document title, the opening paragraph, and throughout the document. ' +
    'Do not use only generic terms like "the Company" or "the Platform" — use the actual brand name.',

  'fb-sections':
    'CRITICAL FIX — NO SECTION STRUCTURE: Your draft lacks proper section headings. ' +
    'Use markdown ## headings to create at least 3 distinct sections with numbered headings.',

  'fb-length-min':
    'CRITICAL FIX — DOCUMENT TOO SHORT: Your draft is under 500 characters. ' +
    'This is a legal document that requires comprehensive coverage. Generate the full document with all sections.',

  'fb-no-duplicate':
    'FIX — DUPLICATE CONTENT: Your draft contains repeated paragraphs or duplicate section headings. ' +
    'Each section must appear EXACTLY ONCE. Do not output the document skeleton followed by the full document. ' +
    'Output only one complete document with no repeated content.',

  'fb-no-meta':
    'FIX — AI COMMENTARY: Your draft includes phrases like "Here is the..." or "I\'ve drafted..." ' +
    'Output ONLY the document itself. No introductions, no commentary, no notes after the document.',

  'fb-no-todo':
    'FIX — TODO MARKERS: Your draft contains TODO, FIXME, or PLACEHOLDER markers. ' +
    'Remove all such markers and replace them with actual content.',

  // v5: Provision depth failures
  'prov-shallow-content':
    'CRITICAL FIX — SHALLOW PROVISION COVERAGE: One or more inherited provisions (CPOM firewall, IP licensed model, ' +
    'clinical liability carve-out, or consent model) appear as keywords only without substantive legal language. ' +
    'Each provision must include 2-3 sentences of legally meaningful content, not just a mention.',

  // Common custom gate structural provisions
  'tos-no-bracket-placeholders':
    'CRITICAL FIX — BRACKET PLACEHOLDERS: Same as above — remove ALL [bracket] notation. Use actual values or {{mustache}} format.',
};

// ── FIX PROMPT (for auto-regeneration) ──

/**
 * Build a prompt that tells Claude which provisions were
 * missing so it can fix the document in the next attempt.
 * Includes provision-specific remediation instructions for
 * structural issues (brackets, duplicates, brand name, etc.)
 */
export function buildFixPrompt(
  originalPrompt: string,
  auditReport: AuditReport,
  brandName?: string,
): string {
  const failedSections = auditReport.sections.filter((s) => !s.found);

  // Separate structural fixes (have remediation) from content fixes (keyword-based)
  const remediations: string[] = [];
  const contentGaps: string[] = [];

  for (const s of failedSections) {
    const remediation = PROVISION_REMEDIATION[s.id];
    if (remediation) {
      // Inject brand name into remediation if applicable
      let fix = remediation;
      if (s.id === 'fb-brand-name' && brandName) {
        fix += ` The brand name is "${brandName}".`;
      }
      remediations.push(fix);
    } else {
      contentGaps.push(`- ${s.title} [${s.weight}]: missing keywords — ${s.missingKeywords.join(', ')}`);
    }
  }

  // v5: Add provision coverage failures to fix prompt
  const provCoverage = auditReport.provisionCoverage || [];
  const failedProvisions = provCoverage.filter(p => p.score < 70);
  if (failedProvisions.length > 0) {
    remediations.push(PROVISION_REMEDIATION['prov-shallow-content']);
    for (const p of failedProvisions) {
      contentGaps.push(
        `- ${p.provisionName}: missing keywords — ${p.keywordsMissing.join(', ')}` +
        (!p.contentDepthPassed ? ' (needs substantive content, not just a mention)' : ''),
      );
    }
  }

  // v5: Add entity issues to fix prompt
  if (auditReport.entityIssues && auditReport.entityIssues.length > 0) {
    contentGaps.push('');
    contentGaps.push('ENTITY REFERENCE ISSUES:');
    for (const issue of auditReport.entityIssues) {
      contentGaps.push(`- ${issue}`);
    }
  }

  const parts = [originalPrompt, '', `--- QUALITY GATE: FIX REQUIRED (Attempt ${auditReport.attempt + 1}) ---`, ''];
  parts.push(`Your previous draft scored ${auditReport.score}%. The following issues MUST be fixed:`);
  parts.push('');

  if (remediations.length > 0) {
    parts.push('=== STRUCTURAL FIXES (fix these FIRST — they affect the entire document) ===');
    parts.push('');
    for (const r of remediations) {
      parts.push(r);
      parts.push('');
    }
  }

  if (contentGaps.length > 0) {
    parts.push('=== MISSING CONTENT PROVISIONS ===');
    parts.push('');
    parts.push(...contentGaps);
    parts.push('');
  }

  parts.push('Please regenerate the COMPLETE document from scratch, fixing ALL issues above.');
  parts.push('Do NOT abbreviate or skip sections. Output the full document with every section included.');
  parts.push('');
  parts.push('--- END FIX INSTRUCTIONS ---');

  return parts.join('\n');
}

// ── GATE ORCHESTRATOR ──

/**
 * Run the full quality gate: score → check → return verdict.
 * v5: Enhanced with provision coverage, entity validation,
 * progressive fill staging, and plain-English summary.
 * The caller handles regeneration loop.
 */
export function runGate(
  content: string,
  gate: QualityGate,
  attempt: number,
  templateId?: string,
): AuditReport {
  const report = scoreDocument(content, gate.provisions);

  // Universal structural checks — apply to ALL documents regardless of custom gate
  const placeholderMatches = content.match(/\{\{[a-zA-Z]+\}\}/g) || [];
  if (placeholderMatches.length > 0) {
    const unique = [...new Set(placeholderMatches)];
    report.sections.push({
      id: 'univ-no-placeholders',
      title: `Unresolved Placeholders (${unique.length})`,
      found: false,
      weight: 'critical',
      matchedKeywords: [],
      missingKeywords: [`${unique.length} {{variables}} need values`],
    });
    report.missingCritical.push(`Unresolved Placeholders (${unique.length})`);
    // Deduct up to 15 points based on placeholder count
    report.score = Math.max(0, report.score - Math.min(15, unique.length));
  }

  const bracketMatches = content.match(/\[(?:INSERT|TBD|PLACEHOLDER|Address|Date|City|State|Name)\b/gi) || [];
  if (bracketMatches.length > 0) {
    report.sections.push({
      id: 'univ-no-brackets',
      title: 'Bracket Placeholders Found',
      found: false,
      weight: 'critical',
      matchedKeywords: [],
      missingKeywords: ['no [INSERT]/[Address]/[Date] placeholders'],
    });
    report.missingCritical.push('Bracket Placeholders Found');
    report.score = Math.max(0, report.score - 10);
  }

  // v5: Inherited provision coverage check
  if (templateId) {
    const provCoverage = scoreProvisionCoverage(content, templateId);
    report.provisionCoverage = provCoverage;

    // Deduct score for weak provision coverage
    const failedProvisions = provCoverage.filter(p => p.score < 70);
    if (failedProvisions.length > 0) {
      const deduction = Math.min(15, failedProvisions.length * 5);
      report.score = Math.max(0, report.score - deduction);

      for (const p of failedProvisions) {
        report.sections.push({
          id: `prov-${p.provisionId}`,
          title: `${p.provisionName} — weak coverage`,
          found: false,
          weight: 'critical',
          matchedKeywords: p.keywordsFound,
          missingKeywords: p.keywordsMissing.length > 0
            ? p.keywordsMissing
            : ['(keywords present but no substantive content)'],
        });
        report.missingCritical.push(`${p.provisionName} — weak coverage`);
      }
    }

    // v5: Entity reference validation
    const entityIssues = validateEntityReferences(content, templateId);
    report.entityIssues = entityIssues;
    if (entityIssues.length > 0) {
      const deduction = Math.min(10, entityIssues.length * 3);
      report.score = Math.max(0, report.score - deduction);

      report.sections.push({
        id: 'entity-validation',
        title: `Entity Reference Issues (${entityIssues.length})`,
        found: false,
        weight: 'high',
        matchedKeywords: [],
        missingKeywords: entityIssues.slice(0, 3),
      });
      report.missingHigh.push(`Entity Reference Issues (${entityIssues.length})`);
    }
  }

  // Pass requires BOTH: score >= threshold AND zero critical missing
  report.passed = report.score >= gate.requiredScore && report.missingCritical.length === 0;
  report.attempt = attempt;
  report.maxAttempts = gate.maxAttempts;

  // v5: Progressive fill staging
  const fillResult = assessFillStage(content, report);
  report.fillStage = fillResult.stage;
  report.fillStageScore = fillResult.score;

  // v5: Plain-English summary
  report.summary = buildGateSummary(report);

  return report;
}

// ── FALLBACK SCORER (for documents without custom quality gates) ──

interface FallbackCheck {
  id: string;
  title: string;
  weight: 'critical' | 'high';
  points: number;
  test: (content: string, brandName: string) => boolean;
  keyword: string; // for audit report display
}

const FALLBACK_CHECKS: FallbackCheck[] = [
  // Structure (35 points)
  {
    id: 'fb-sections', title: 'Has section structure (3+ headings)', weight: 'critical', points: 15,
    test: (c) => (c.match(/^#{1,3}\s+/gm) || []).length >= 3,
    keyword: '## section headings',
  },
  {
    id: 'fb-length-min', title: 'Minimum content length (500+ chars)', weight: 'critical', points: 10,
    test: (c) => c.length >= 500,
    keyword: 'substantial content',
  },
  {
    id: 'fb-length-good', title: 'Good content length (1000+ chars)', weight: 'high', points: 10,
    test: (c) => c.length >= 1000,
    keyword: 'detailed content',
  },
  // Completeness (45 points)
  {
    id: 'fb-no-brackets', title: 'No bracket placeholders', weight: 'critical', points: 15,
    test: (c) => {
      // Common explicit placeholder words
      if (/\[(?:INSERT|TBD|PLACEHOLDER|COMPANY|ENTITY)\b/i.test(c)) return false;
      // Address/contact placeholders: [Address], [City, State ZIP], [Phone], [Email], etc.
      if (/\[(?:Address|City|State|ZIP|Zip|Date|Phone|Email|Signature|Title|County|Name|Full Name|Legal Name|Entity Name|Effective Date)\s*[\],]/i.test(c)) return false;
      // Pattern: [City, State ZIP] or [City, State Zip Code]
      if (/\[City,?\s*State/i.test(c)) return false;
      // Generic: [ALLCAPS WORD] not followed by ( (excludes markdown links)
      if (/\[[A-Z]{2,}(?:\s+[A-Z]{2,})*\](?!\()/g.test(c)) return false;
      return true;
    },
    keyword: 'no [Address]/[Date]/[INSERT] placeholders',
  },
  {
    id: 'fb-no-mustache', title: 'No unreplaced {{variables}}', weight: 'critical', points: 10,
    test: (c) => {
      // Match {{variableName}} patterns that should have been replaced by brand config
      const matches = c.match(/\{\{[a-zA-Z]+\}\}/g) || [];
      return matches.length === 0;
    },
    keyword: 'no {{variable}} placeholders',
  },
  {
    id: 'fb-no-todo', title: 'No TODO/FIXME markers', weight: 'high', points: 10,
    test: (c) => !/\bTODO\b|\bFIXME\b|\bPLACEHOLDER\b/i.test(c),
    keyword: 'no TODO markers',
  },
  {
    id: 'fb-proper-ending', title: 'Proper document ending', weight: 'high', points: 10,
    test: (c) => {
      const trimmed = c.trim();
      // Shouldn't end mid-sentence (trailing comma, open paren, etc.)
      return !/(,|;\s*|\(\s*|\.\.\.)$/.test(trimmed);
    },
    keyword: 'complete ending',
  },
  // Content integrity
  {
    id: 'fb-no-duplicate', title: 'No duplicate content blocks', weight: 'high', points: 10,
    test: (c) => {
      // Check 1: Multiple H1 headings = likely stub + full doc concatenated
      const h1Count = (c.match(/^#\s+[^\n]+$/gm) || []).length;
      if (h1Count > 1) return false;
      // Check 2: Duplicate H2 headings (same heading appearing twice)
      const h2s = (c.match(/^##\s+.+$/gm) || []).map(h => h.trim().toLowerCase());
      const unique = new Set(h2s);
      return h2s.length <= 2 || unique.size >= h2s.length * 0.8;
    },
    keyword: 'no duplicate sections',
  },
  // Brand integration (30 points)
  {
    id: 'fb-brand-name', title: 'References brand name', weight: 'critical', points: 15,
    test: (c, brand) => brand.length > 0 && c.toLowerCase().includes(brand.toLowerCase()),
    keyword: 'brand name reference',
  },
  {
    id: 'fb-legal-language', title: 'Contains legal/professional language', weight: 'high', points: 10,
    test: (c) => {
      const lower = c.toLowerCase();
      const legalTerms = [
        'shall', 'pursuant', 'herein', 'agreement', 'obligation', 'provision',
        'compliance', 'liability', 'indemnif', 'terminate', 'effective date',
        'governing law', 'resolved', 'authorized', 'whereas', 'witness',
        'certif', 'director', 'officer', 'fiduciary', 'hipaa', 'confidential',
        'consent', 'disclosure', 'safeguard', 'breach', 'covered entity',
      ];
      return legalTerms.filter(t => lower.includes(t)).length >= 3;
    },
    keyword: 'legal terminology',
  },
  {
    id: 'fb-no-meta', title: 'No AI meta-commentary', weight: 'high', points: 5,
    test: (c) => {
      const lower = c.toLowerCase();
      return !/(here is the|here's the|i've (created|drafted|generated)|as requested|as you asked|please review the following)/i.test(lower);
    },
    keyword: 'no meta-commentary',
  },
];

/**
 * Fallback quality scorer for documents without custom quality gates.
 * v5: Enhanced with provision coverage, entity validation, fill staging, and summary.
 */
export function runFallbackGate(
  content: string,
  brandName: string,
  templateId?: string,
): AuditReport {
  // Empty or trivial content should not score — return 0
  if (!content || content.trim().length < 100) {
    return {
      score: 0,
      passed: false,
      attempt: 1,
      maxAttempts: 1,
      sections: [],
      missingCritical: ['No substantive content'],
      missingHigh: [],
      timestamp: new Date().toISOString(),
      fillStage: 'skeleton',
      fillStageScore: 10,
      summary: 'Document has no substantive content.',
    };
  }

  const sections: AuditSectionResult[] = [];
  let earnedCritical = 0, totalCritical = 0;
  let earnedHigh = 0, totalHigh = 0;

  for (const check of FALLBACK_CHECKS) {
    const passed = check.test(content, brandName);

    if (check.weight === 'critical') {
      totalCritical += check.points;
      if (passed) earnedCritical += check.points;
    } else {
      totalHigh += check.points;
      if (passed) earnedHigh += check.points;
    }

    sections.push({
      id: check.id,
      title: check.title,
      found: passed,
      weight: check.weight,
      matchedKeywords: passed ? [check.keyword] : [],
      missingKeywords: passed ? [] : [check.keyword],
    });
  }

  // Score: critical checks are 55% of total, high checks are 45%
  const criticalPct = totalCritical > 0 ? (earnedCritical / totalCritical) : 1;
  const highPct = totalHigh > 0 ? (earnedHigh / totalHigh) : 1;
  let score = Math.round(criticalPct * 55 + highPct * 45);

  const missingCritical = sections.filter(s => !s.found && s.weight === 'critical').map(s => s.title);
  const missingHigh = sections.filter(s => !s.found && s.weight === 'high').map(s => s.title);

  // v5: Provision coverage for fallback gate too
  let provisionCoverage: ProvisionCoverageResult[] | undefined;
  let entityIssues: string[] | undefined;
  if (templateId) {
    provisionCoverage = scoreProvisionCoverage(content, templateId);
    entityIssues = validateEntityReferences(content, templateId);

    // Deduct for weak provision coverage
    const failedProvisions = (provisionCoverage || []).filter(p => p.score < 70);
    if (failedProvisions.length > 0) {
      const deduction = Math.min(10, failedProvisions.length * 3);
      score = Math.max(0, score - deduction);
      for (const p of failedProvisions) {
        missingCritical.push(`${p.provisionName} — weak coverage`);
      }
    }

    // Deduct for entity issues
    if (entityIssues && entityIssues.length > 0) {
      score = Math.max(0, score - Math.min(5, entityIssues.length * 2));
    }
  }

  const report: AuditReport = {
    score,
    passed: score >= 70 && missingCritical.length === 0,
    attempt: 1,
    maxAttempts: 1,
    sections,
    missingCritical,
    missingHigh,
    timestamp: new Date().toISOString(),
    provisionCoverage,
    entityIssues,
  };

  // v5: Fill staging and summary
  const fillResult = assessFillStage(content, report);
  report.fillStage = fillResult.stage;
  report.fillStageScore = fillResult.score;
  report.summary = buildGateSummary(report);

  return report;
}
